import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getPortalAccessToken, getPortalRefreshToken, setPortalTokens, clearPortalTokens } from './token';
import { ElMessage } from 'element-plus';
import router from '@/router';

type PortalRetryConfig = AxiosRequestConfig & { _portalRetried?: boolean };

// 防止重复跳转登录页
let isRedirectingToLogin = false;

// 跳转到登录页
const redirectToLogin = (showMessage = true) => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  
  if (showMessage) {
    ElMessage.error('登录已过期，请重新登录');
  }
  clearPortalTokens();
  router.push({
    path: '/login',
    query: { redirect: router.currentRoute.value.fullPath }
  });
  
  // 2秒后重置标记，允许下次跳转
  setTimeout(() => {
    isRedirectingToLogin = false;
  }, 2000);
};

const portalService: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 8000,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

portalService.interceptors.request.use(
  (config) => {
    const token = getPortalAccessToken();
    config.headers = config.headers ?? {};
    if (token) {
      (config.headers as any)['Authorization'] = `Portal ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

portalService.interceptors.response.use(
  async (response: AxiosResponse) => {
    const res = response.data;
    if (res.code === 200) {
      return res;
    }

    const originalConfig = response.config as PortalRetryConfig;
    if (res.code === 401 && !originalConfig._portalRetried) {
      originalConfig._portalRetried = true;
      const refreshToken = getPortalRefreshToken();
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(new Error(res.msg || '未登录'));
      }

      try {
        const refreshRes: any = await axios.request({
          baseURL: process.env.VUE_APP_BASE_API || '/api',
          url: '/api/portal/auth/refresh',
          method: 'post',
          data: { refreshToken }
        });
        const refreshData = refreshRes.data;
        if (refreshData.code !== 200) {
          redirectToLogin();
          return Promise.reject(new Error(refreshData.msg || '登录已失效'));
        }
        const pair = refreshData.data;
        setPortalTokens(pair.accessToken, pair.refreshToken);
        return portalService.request(originalConfig);
      } catch (e: any) {
        // 刷新请求失败（网络错误或服务器错误），静默处理并跳转登录
        redirectToLogin(false);
        return Promise.reject(new Error('登录已失效，请重新登录'));
      }
    }

    return Promise.reject(new Error(res?.msg ?? res?.message ?? '请求失败'));
  },
  (error) => Promise.reject(error)
);

export default portalService;

